import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Alert, Button, FormField, HelpPanel, Select } from '@amzn/awsui-components-react';
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { SupportedLocale } from '@amzn/red-velvet-api';
import { useTranslation } from 'react-i18next';
import { P0_SUPPORTED_LANGUAGES } from '../../data/supportedLanguages';
import { resolvePickerLocale, toOfferedLocale } from '../../utils/localeUtils';
import { useGetUserPreference, useUpdateUserPreference } from '../../hooks/userPreferences';
import { publishKatalMetric } from '../katalAnalytics';
import { useUser } from '../../hooks/user';
import { writeCachedLocale } from '../../utils/localePreferenceCache';
import {
  LANGUAGE_PICKER_CONTROL_ID,
  LANGUAGE_PICKER_DROPDOWN,
  LANGUAGE_PICKER_SELECT,
} from '../../utils/constants';

const I18N = 'languagePicker';

const LANGUAGE_OPTIONS: SelectProps.Option[] = P0_SUPPORTED_LANGUAGES.map(({ locale, label }) => ({
  value: locale,
  label,
}));

export type LanguagePickerPanelProps = {
  onDismiss: () => void;
};

function browserLanguages(): readonly string[] {
  return navigator.languages?.length
    ? navigator.languages
    : navigator.language
      ? [navigator.language]
      : [];
}

/**
 * The "Change language" panel opened from the alias menu.
 *
 * Shows the user's language, falling back to their browser's when they have never chosen one, and
 * reloads the page once a new choice is saved so it takes effect everywhere.
 *
 */
export function LanguagePickerPanel({ onDismiss }: LanguagePickerPanelProps) {
  const { t } = useTranslation();
  const { userId } = useUser();
  const preferenceQuery = useGetUserPreference();
  const updatePreference = useUpdateUserPreference();
  const panelRef = useRef<HTMLElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const [pendingLocale, setPendingLocale] = useState<SupportedLocale | undefined>();

  useEffect(() => {
    // The menu that opened the panel has closed, so focus needs somewhere to land.
    openerRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
  }, []);

  const dismiss = () => {
    // Only on a deliberate close. The panel also closes on navigation, where taking focus back would
    // pull the user away from the page they just opened.
    if (openerRef.current?.isConnected) {
      openerRef.current.focus();
    }
    onDismiss();
  };

  // What the user is actually set to, as opposed to the fallback shown when that is unknown.
  const knownPersistedLocale = preferenceQuery.isError
    ? undefined
    : toOfferedLocale(preferenceQuery.data?.locale);
  const resolvedLocale = resolvePickerLocale(preferenceQuery.data?.locale, browserLanguages());
  const selectedLocale = pendingLocale ?? resolvedLocale;
  const selectedOption = preferenceQuery.isLoading
    ? null
    : (LANGUAGE_OPTIONS.find((option) => option.value === selectedLocale) ?? null);

  const onLanguageChange: SelectProps['onChange'] = ({ detail }) => {
    const locale = toOfferedLocale(detail.selectedOption.value);
    // Compared against the saved language, not the one on screen, so confirming a fallback still
    // saves it.
    if (!locale || locale === knownPersistedLocale) {
      return;
    }

    setPendingLocale(locale);
    publishKatalMetric(LANGUAGE_PICKER_SELECT, locale);
    updatePreference.mutate(
      { locale },
      {
        onSuccess: () => {
          writeCachedLocale(userId, locale);
          window.location.reload();
        },
        onError: () => setPendingLocale(undefined),
      }
    );
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape') {
      return;
    }
    // Escape from the open dropdown closes only the dropdown; a second one closes the panel.
    if (!panelRef.current?.contains(event.target as Node)) {
      return;
    }
    dismiss();
  };

  return (
    <aside
      ref={panelRef}
      tabIndex={-1}
      className="vc-language-panel"
      aria-label={t(`${I18N}.title`)}
      onKeyDown={onKeyDown}
      data-testid="language-picker-panel"
    >
      <div className="vc-language-panel__dismiss">
        <Button
          variant="icon"
          iconName="close"
          ariaLabel={t(`${I18N}.close`)}
          onClick={dismiss}
          data-testid="language-picker-close"
        />
      </div>
      <HelpPanel header={<h2>{t(`${I18N}.title`)}</h2>}>
        <FormField
          // Fixed id. App.scss uses it to cap the list height.
          controlId={LANGUAGE_PICKER_CONTROL_ID}
          label={t(`${I18N}.label`)}
          i18nStrings={{ errorIconAriaLabel: t(`${I18N}.errorIconLabel`) }}
          // Shown in red under the picker, and read out, when the save fails.
          errorText={updatePreference.isError ? t(`${I18N}.error`) : undefined}
        >
          <Select
            data-testid="language-picker-select"
            data-aci-analytics-name={LANGUAGE_PICKER_DROPDOWN}
            ariaLabel={t(`${I18N}.label`)}
            selectedOption={selectedOption}
            options={LANGUAGE_OPTIONS}
            onChange={onLanguageChange}
            filteringType="auto"
            filteringPlaceholder={t(`${I18N}.filteringPlaceholder`)}
            placeholder={t(`${I18N}.loading`)}
            disabled={preferenceQuery.isLoading || updatePreference.isPending}
            // Keeps the list clear of the panel's edges, and keeps Escape closing one layer at a time.
            expandToViewport={true}
          />
        </FormField>
        {preferenceQuery.isError && (
          <Alert
            type="warning"
            statusIconAriaLabel={t(`${I18N}.warningIconLabel`)}
            data-testid="language-picker-read-error"
          >
            {t(`${I18N}.readError`)}
          </Alert>
        )}
      </HelpPanel>
    </aside>
  );
}
